import type { ReactNode } from "react";
import type { GestureResponderEvent } from "react-native";
import { AnimatePresence, type ColorTokens, type GetProps, styled, View, XStack } from "tamagui";
import { useMinimumLoadingTime } from "../../utils";
import { Icon, type IconComponent } from "../Icon";
import { useInteractiveElement } from "../interactive/useInteractiveElement";
import { Loader } from "../Loader";
import type { Press } from "../Motion/motion";
import { Interface1, Interface2, Interface3 } from "../Typography";
import { BrandButtonGradient } from "./BrandButtonGradient";
import type { ButtonSize, ButtonTone, ButtonVariant } from "./types";
import { useBrandButtonGradient } from "./useBrandButtonGradient";
import { buttonSizeToIconToken, buttonVariants, darkToneIconColorTokens, darkToneTextColorTokens, darkToneVariantStyles, disabledIconColorTokens, disabledTextColorTokens, disabledVariantStyles, iconColorTokens, iconOnlySizes, iconPaddingBySize, textColorTokens } from "./utils";
export type { ButtonSize, ButtonTone, ButtonVariant } from "./types";
const StyledButton = styled(View, {
  name: "Button",
  render: "button",
  focusable: true,
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  borderRadius: "$brFull",
  // `position: relative` + `overflow: hidden` create a stacking context so the
  // BrandButtonGradient overlay stays clipped within the button's rounded bounds.
  position: "relative",
  overflow: "hidden",
  transition: "press",
  variants: buttonVariants,
  defaultVariants: {
    variant: "primary",
    size: "medium",
    fillWidth: true,
    press: "default"
  } as const
});

// `animateOnly` must be mutable: Tamagui rejects `readonly string[]`.
const opacityFade = {
  transition: "fade" as const,
  animateOnly: ["opacity"]
};
const buttonTextStyles = {
  name: "ButtonText",
  ...opacityFade
};
const ButtonTextLarge = styled(Interface1, buttonTextStyles);
const ButtonTextMedium = styled(Interface2, buttonTextStyles);
const ButtonTextSmall = styled(Interface3, buttonTextStyles);
const IconWrapper = styled(View, {
  name: "IconWrapper",
  ...opacityFade
});

// Overlay that fades in/out with AnimatePresence when the button is loading.
// Absolute so it covers the hidden (opacity: 0) text/icons without affecting layout.
const LoaderOverlay = styled(View, {
  name: "LoaderOverlay",
  position: "absolute",
  inset: 0,
  justifyContent: "center",
  alignItems: "center",
  opacity: 1,
  enterStyle: {
    opacity: 0
  },
  exitStyle: {
    opacity: 0
  },
  ...opacityFade
});
type StyledButtonProps = GetProps<typeof StyledButton>;
type BaseButtonProps = Omit<StyledButtonProps, "variant" | "size" | "onPressOut" | "children" | "onPress" | "href" | "render" | "role" | "press"> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Press feedback. @default "default" */
  press?: Press;
  /**
   * Set `"dark"` when the button sits on `$surfaceInverse`, so its glyph and
   * interaction surface stay visible there. Currently only changes `tertiary`
   * (see `darkToneVariantStyles`).
   * @default "light"
   */
  tone?: ButtonTone;
  loading?: boolean;
  /**
   * Skip the minimum loading display time (400ms) and transition instantly.
   * @default false
   */
  instantLoading?: boolean;
};

/**
 * Icon-only buttons: pass `aria-label` for screen readers.
 * Omit only for purely decorative icons.
 */
type IconOnlyButtonProps = BaseButtonProps & {
  icon: IconComponent;
  children?: never;
  iconLeft?: never;
  iconRight?: never;
};
type TextButtonProps = BaseButtonProps & {
  icon?: never;
  children: ReactNode;
  iconLeft?: IconComponent;
  iconRight?: IconComponent;
};
type VisualProps = IconOnlyButtonProps | TextButtonProps;
type BehaviorProps = {
  onPress?: (event: GestureResponderEvent) => void;
  href?: string;
  replace?: boolean;
  scroll?: boolean;
  shallow?: boolean;
};
export type ButtonProps = VisualProps & BehaviorProps;
export const Button = (props: ButtonProps) => {
  const {
    variant = "primary",
    size = "medium",
    tone = "light",
    loading,
    instantLoading = false,
    disabled,
    icon,
    iconLeft,
    iconRight,
    fillWidth = true,
    children,
    onPress,
    href,
    replace,
    scroll,
    shallow,
    testID,
    ...rest
  } = props;
  const isIconOnly = icon !== undefined;
  const defaultTestID = typeof children === "string" ? `${variant}-button-${children}` : `${variant}-button`;
  const resolvedTestID = testID ?? defaultTestID;
  const debouncedLoading = useMinimumLoadingTime(loading);
  const showLoading = instantLoading ? loading ?? false : debouncedLoading;
  const isInteractionDisabled = disabled || showLoading;
  const isBrand = variant === "brand";
  const brandGradient = useBrandButtonGradient({
    variant,
    isInteractionDisabled
  });

  // Always interactive — Button renders as a real `<button>` (or `<a>` for
  // href) with focus + keyboard wiring even when no `onPress` is supplied.
  const {
    interactiveProps,
    renderTag
  } = useInteractiveElement({
    kind: href !== undefined ? "link" : "button",
    onPress,
    href,
    replace,
    scroll,
    shallow,
    disabled: isInteractionDisabled,
    haptic: true
  });
  const handlePressOut = brandGradient.handleBrandPressOut;
  const buttonTextBySize = {
    large: ButtonTextLarge,
    medium: ButtonTextMedium,
    small: ButtonTextSmall
  };
  const ButtonText = buttonTextBySize[size];
  const iconSize = buttonSizeToIconToken[size];
  // Disabled outranks tone: a disabled button reads as disabled on any surface.
  const isDark = tone === "dark";
  const textColor: ColorTokens = disabled ? disabledTextColorTokens[variant] : (isDark ? darkToneTextColorTokens[variant] : undefined) ?? textColorTokens[variant];
  const iconColor: ColorTokens = disabled ? disabledIconColorTokens[variant] : (isDark ? darkToneIconColorTokens[variant] : undefined) ?? iconColorTokens[variant];
  const toneStyles = isDark && !disabled ? darkToneVariantStyles[variant] ?? {} : {};
  const disabledStyles = disabled ? disabledVariantStyles[variant] : {};

  // For icon-only buttons, make width equal to height (square) and remove padding
  const iconOnlyStyles = isIconOnly ? {
    width: iconOnlySizes[size],
    paddingHorizontal: 0
  } : {};

  // Asymmetric padding when icons present (optically centered per design spec).
  // Use reduced padding on whichever side has an icon.
  const hasIcons = !isIconOnly && (iconLeft || iconRight);
  const iconPaddingStyles = hasIcons ? {
    paddingLeft: iconLeft ? iconPaddingBySize[size].iconSide : iconPaddingBySize[size].textSide,
    paddingRight: iconRight ? iconPaddingBySize[size].iconSide : iconPaddingBySize[size].textSide
  } : {};
  return <StyledButton {...rest} testID={resolvedTestID} render={renderTag ?? "button"} variant={variant} size={size} disabled={isInteractionDisabled} fillWidth={isIconOnly ? false : fillWidth}
  // Gradient handlers only for brand — avoids registering ~10 noop DOM listeners on every non-brand button.
  {...isBrand && {
    onLayout: brandGradient.handleBrandLayout,
    onMouseEnter: brandGradient.handleBrandMouseEnter,
    onMouseMove: brandGradient.handleBrandMouseMove,
    onMouseLeave: brandGradient.handleBrandMouseLeave,
    onTouchStart: brandGradient.handleBrandTouchStart,
    onTouchMove: brandGradient.handleBrandTouchMove,
    onTouchEnd: brandGradient.handleBrandTouchEnd,
    onTouchCancel: brandGradient.handleBrandTouchEnd,
    onResponderRelease: brandGradient.handleBrandTouchEnd,
    onResponderTerminate: brandGradient.handleBrandTouchEnd,
    onPressIn: brandGradient.handleBrandPressIn
  }} onPressOut={handlePressOut} {...toneStyles} {...disabledStyles} {...iconOnlyStyles} {...iconPaddingStyles} {...isInteractionDisabled && {
    cursor: "not-allowed"
  }} {...interactiveProps} data-sentry-element="StyledButton" data-sentry-component="Button" data-sentry-source-file="Button.tsx">
      {isBrand ? <BrandButtonGradient visible={brandGradient.showBrandHoverGradient} style={brandGradient.brandGradientStyle} /> : null}
      <XStack justifyContent="center" alignItems="center" gap="$sp1" position="relative" zIndex={1} pointerEvents="none" data-sentry-element="XStack" data-sentry-source-file="Button.tsx">
        {icon ? <IconWrapper opacity={showLoading ? 0 : 1} style={{
        outline: "none",
        pointerEvents: "none"
      }}>
            <Icon icon={icon} size={iconSize} color={iconColor} />
          </IconWrapper> : null}
        {iconLeft ? <IconWrapper opacity={showLoading ? 0 : 1}>
            <Icon icon={iconLeft} size={iconSize} color={iconColor} />
          </IconWrapper> : null}
        {children ? <ButtonText color={textColor} opacity={showLoading ? 0 : 1}>
            {children}
          </ButtonText> : null}
        {iconRight ? <IconWrapper opacity={showLoading ? 0 : 1}>
            <Icon icon={iconRight} size={iconSize} color={iconColor} />
          </IconWrapper> : null}
        <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="Button.tsx">
          {showLoading ? <LoaderOverlay>
              <Loader color={textColor} size={iconSize} />
            </LoaderOverlay> : null}
        </AnimatePresence>
      </XStack>
    </StyledButton>;
};